import { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { InfoCard } from "@/components/toggles";
import { useAuth } from "@/contexts/AuthContext";
import api from "@/lib/api";
import { ArrowLeft, Package, Download, RefreshCw, CheckCircle, Clock, AlertTriangle, ChevronDown, ChevronUp } from "lucide-react";

const STATUS_META = {
  CREATED: { label: "Erstellt", color: "bg-gray-100 text-gray-700 border-gray-300" },
  AWAITING_SHIPMENT: { label: "Warte auf Versand", color: "bg-amber-100 text-amber-800 border-amber-300" },
  SELLER_DISPATCHED: { label: "Versendet", color: "bg-blue-100 text-blue-800 border-blue-300" },
  SCANNED_AT_HUB: { label: "Im Hub eingegangen", color: "bg-indigo-100 text-indigo-800 border-indigo-300" },
  INBOUND_PARTIAL: { label: "Im Hub – teilweise geprüft", color: "bg-amber-100 text-amber-800 border-amber-300" },
  HOLD_REVIEW: { label: "In Klärung", color: "bg-red-100 text-red-800 border-red-300" },
  RECEIPT_CONFIRMED: { label: "Eingang bestätigt", color: "bg-green-100 text-green-800 border-green-300" },
  READY_FOR_OUTBOUND: { label: "Bereit zum Versand an Käufer", color: "bg-purple-100 text-purple-800 border-purple-300" },
  OUTBOUND_SHIPPED: { label: "Versendet an Käufer", color: "bg-blue-100 text-blue-800 border-blue-300" },
  CLOSED: { label: "Abgeschlossen", color: "bg-green-100 text-green-800 border-green-300" },
};

export default function SellerShipmentsPage() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { user, loading: authLoading } = useAuth();
  const [shipments, setShipments] = useState([]);
  const [ledger, setLedger] = useState(null);
  const [loading, setLoading] = useState(true);
  const [expandedId, setExpandedId] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [{ data: shipData }, { data: ledgerData }] = await Promise.all([
        api.get("/seller/shipments").catch(() => ({ data: { items: [] } })),
        api.get("/seller/ledger").catch(() => ({ data: null })),
      ]);
      setShipments(shipData.items || shipData.shipments || []);
      setLedger(ledgerData);
    } catch { /* ignore */ }
    finally { setLoading(false); }
  }, []);

  useEffect(() => {
    if (authLoading) return;
    if (!user || user === false) { navigate("/login"); return; }
    load();
  }, [user, authLoading, navigate, load]);

  const confirmShipped = async (sid) => {
    try {
      await api.post(`/seller/shipments/${sid}/dispatch`, {
        shipping_method: "STANDARD",
        tracking_number: "",
      });
      load();
    } catch (e) {
      alert(e?.response?.data?.detail || "Fehler beim Markieren als versendet");
    }
  };

  return (
    <div data-testid="seller-shipments-page" className="min-h-screen bg-white">
      {/* Header */}
      <div className="flex items-center gap-3 px-4 py-3 border-b border-[#E5E5E5]">
        <button data-testid="back-btn" onClick={() => navigate(-1)}><ArrowLeft size={20} /></button>
        <div className="flex-1">
          <h1 className="text-sm font-bold text-[#0A0A0A] flex items-center gap-1.5">
            <Package size={14} /> Meine Versand-Sendungen
          </h1>
          <p className="text-[10px] text-[#999]">Inbound-Shipments + QR-Codes</p>
        </div>
        <button onClick={load} className="p-1.5 text-[#999] hover:text-[#0A0A0A]" aria-label="Aktualisieren">
          <RefreshCw size={14} />
        </button>
      </div>

      {/* Ledger Summary */}
      {ledger && (
        <div className="grid grid-cols-3 border-b border-[#E5E5E5]">
          <KPIBox label={t("seller_shipments.available")} value={`${(ledger.balance ?? 0).toFixed(2)} €`} hint="Guthaben" />
          <KPIBox label="Gutgeschrieben" value={`${(ledger.total_credit ?? 0).toFixed(2)} €`} hint="Insgesamt" />
          <KPIBox label="Ausgezahlt" value={`${(ledger.total_payout ?? 0).toFixed(2)} €`} hint="Insgesamt" last />
        </div>
      )}

      {/* List */}
      <div className="px-4 py-4">
        {loading ? (
          <div className="py-12 flex justify-center">
            <div className="w-6 h-6 border-2 border-[#E5E5E5] border-t-[#0A0A0A] rounded-full animate-spin" />
          </div>
        ) : shipments.length === 0 ? (
          <div className="py-12 text-center">
            <Package size={32} className="mx-auto text-[#CCC] mb-2" />
            <p className="text-sm text-[#666]">{t("seller_shipments.empty")}</p>
            <p className="text-[11px] text-[#999] mt-1">{t("seller_shipments.empty_hint")}</p>
          </div>
        ) : (
          <div className="space-y-3">
            {shipments.map((s) => (
              <ShipmentCard
                key={s.inbound_shipment_id}
                shipment={s}
                expanded={expandedId === s.inbound_shipment_id}
                onToggle={() => setExpandedId(expandedId === s.inbound_shipment_id ? null : s.inbound_shipment_id)}
                onConfirmShipped={() => confirmShipped(s.inbound_shipment_id)}
              />
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

function KPIBox({ label, value, hint, last }) {
  return (
    <div className={`px-3 py-3 ${last ? "" : "border-r border-[#E5E5E5]"}`}>
      <p className="text-[9px] text-[#999] uppercase tracking-wider">{label}</p>
      <p className="text-sm font-bold text-[#0A0A0A] mt-0.5">{value}</p>
      {hint && <p className="text-[9px] text-[#999]">{hint}</p>}
    </div>
  );
}

function ShipmentCard({ shipment, expanded, onToggle, onConfirmShipped }) {
  const meta = STATUS_META[shipment.status] || { label: shipment.status, color: "bg-gray-100 text-gray-600" };
  const canMarkShipped = ["CREATED", "AWAITING_SHIPMENT"].includes(shipment.status);
  const showQR = !["RECEIPT_CONFIRMED", "READY_FOR_OUTBOUND", "OUTBOUND_SHIPPED", "CLOSED"].includes(shipment.status);
  const sid = shipment.inbound_shipment_id;

  return (
    <div data-testid={`shipment-${sid}`} className="border border-[#E5E5E5]">
      {/* Header */}
      <button onClick={onToggle} className="w-full px-3 py-3 flex items-center justify-between hover:bg-[#FAFAFA]">
        <div className="flex-1 text-left min-w-0">
          <div className="flex items-center gap-2 mb-1">
            <span className={`px-2 py-0.5 text-[10px] font-medium border ${meta.color}`}>{meta.label}</span>
          </div>
          <p className="text-xs text-[#0A0A0A] truncate">
            {shipment.order?.title || `Order ${shipment.order_id?.slice(0,8)}`}
          </p>
          <p className="text-[10px] text-[#999]">
            {shipment.expected_item_count || 0} Artikel · ID {sid.slice(0,8)}…
          </p>
        </div>
        {expanded ? <ChevronUp size={16} className="text-[#999] shrink-0" /> : <ChevronDown size={16} className="text-[#999] shrink-0" />}
      </button>

      {/* Expanded Content */}
      {expanded && (
        <div className="border-t border-[#E5E5E5] px-3 py-3 bg-[#FAFAFA]">
          {showQR ? (
            <>
              <QRBlock shipment={shipment} />
              {canMarkShipped && (
                <button data-testid={`mark-shipped-${sid}`}
                  onClick={onConfirmShipped}
                  className="mt-3 w-full rounded-full h-10 bg-[#0A0A0A] text-white text-xs font-semibold flex items-center justify-center gap-1.5 hover:bg-[#222]">
                  <CheckCircle size={14} /> Ich habe versendet
                </button>
              )}
              {shipment.status === "SELLER_DISPATCHED" && (
                <InfoCard variant="info" className="mt-3">
                  <Clock size={12} className="text-blue-600 mt-0.5 shrink-0" />
                  <p className="text-[11px] text-blue-700">
                    Du hast als versendet markiert. Sobald das Paket im Hub eingeht, wirst du benachrichtigt.
                  </p>
                </InfoCard>
              )}
            </>
          ) : (
            <PostHubStatus shipment={shipment} />
          )}

          {/* Audit-Trail kurz */}
          {shipment.history && shipment.history.length > 0 && (
            <details className="mt-3">
              <summary className="text-[10px] text-[#666] cursor-pointer">Verlauf ({shipment.history.length} Einträge)</summary>
              <ul className="mt-2 space-y-1">
                {shipment.history.map((h, i) => (
                  <li key={i} className="text-[10px] text-[#666]">
                    {h.created_at ? new Date(h.created_at).toLocaleString("de-DE", { dateStyle: "short", timeStyle: "short" }) : "—"}
                    {" · "}{h.from_status} → {h.to_status}
                  </li>
                ))}
              </ul>
            </details>
          )}
        </div>
      )}
    </div>
  );
}

function QRBlock({ shipment }) {
  const { t } = useTranslation();
  const sid = shipment.inbound_shipment_id;
  const REACT_APP_BACKEND_URL = process.env.REACT_APP_BACKEND_URL || "";
  const token = localStorage.getItem("token") || "";

  // Build authenticated image URLs
  // Authenticated image fetching → use fetch + blob URL
  const [qrUrl, setQrUrl] = useState(null);
  const [pdfDownloading, setPdfDownloading] = useState(false);

  useEffect(() => {
    let revokeUrl = null;
    (async () => {
      try {
        const res = await fetch(`${REACT_APP_BACKEND_URL}/api/seller/shipments/${sid}/qr.png`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        if (!res.ok) return;
        const blob = await res.blob();
        const url = URL.createObjectURL(blob);
        revokeUrl = url;
        setQrUrl(url);
      } catch { /* ignore */ }
    })();
    return () => { if (revokeUrl) URL.revokeObjectURL(revokeUrl); };
  }, [REACT_APP_BACKEND_URL, sid, token]);

  const downloadPdf = async () => {
    setPdfDownloading(true);
    try {
      const res = await fetch(`${REACT_APP_BACKEND_URL}/api/seller/shipments/${sid}/insert.pdf`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!res.ok) { alert(t("seller_shipments.pdf_error")); return; }
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `paket-einleger-${sid.slice(0,8)}.pdf`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
    } finally { setPdfDownloading(false); }
  };

  return (
    <div className="bg-white border border-[#E5E5E5] p-4 flex flex-col items-center">
      <p className="text-[10px] text-[#999] uppercase tracking-wider mb-2">{t("seller_shipments.qr_for_insert")}</p>
      {qrUrl ? (
        <img src={qrUrl} alt="QR-Code" className="w-44 h-44 object-contain" />
      ) : (
        <div className="w-44 h-44 bg-[#FAFAFA] border border-[#E5E5E5] flex items-center justify-center">
          <div className="w-6 h-6 border-2 border-[#E5E5E5] border-t-[#0A0A0A] rounded-full animate-spin" />
        </div>
      )}
      <p className="mt-2 text-[10px] text-[#999]">Fallback-Code (falls QR unscannbar):</p>
      <p className="text-base font-bold font-mono text-[#0A0A0A] mt-0.5 tracking-wider">{shipment.qr_fallback_code || "—"}</p>

      <button data-testid={`download-pdf-${shipment.inbound_shipment_id}`}
        onClick={downloadPdf} disabled={pdfDownloading}
        className="mt-3 rounded-full h-9 px-4 border border-[#0A0A0A] text-[#0A0A0A] text-xs font-semibold flex items-center gap-1.5 hover:bg-[#0A0A0A] hover:text-white disabled:opacity-50">
        <Download size={12} /> {pdfDownloading ? "Lädt..." : "PDF-Einleger herunterladen"}
      </button>

      <InfoCard variant="warning" className="mt-3 w-full">
        <p className="text-[10px] text-amber-900 leading-relaxed">
          <strong>Anleitung:</strong> Drucke den PDF-Einleger aus und lege ihn ins Paket.
          Versende an die Hub-Adresse. <strong>{t("seller_shipments.no_name_on_pkg")}</strong> – die Plattform sorgt für anonyme Weiterleitung.
        </p>
      </InfoCard>
    </div>
  );
}

function PostHubStatus({ shipment }) {
  const { t } = useTranslation();
  if (shipment.status === "HOLD_REVIEW") {
    return (
      <InfoCard variant="danger">
        <div className="flex items-start gap-2">
          <AlertTriangle size={14} className="text-red-600 mt-0.5 shrink-0" />
          <div>
            <p className="text-xs font-bold text-red-700">Im Hub wurde ein Problem festgestellt.</p>
            <p className="text-[11px] text-red-700 mt-1">
              Unser Team prüft das Paket. Du wirst benachrichtigt, sobald geklärt.
            </p>
          </div>
        </div>
      </InfoCard>
    );
  }
  return (
    <InfoCard variant="success">
      <div className="flex items-start gap-2">
        <CheckCircle size={14} className="text-green-600 mt-0.5 shrink-0" />
        <div>
          <p className="text-xs font-bold text-green-700">Paket im Hub verarbeitet.</p>
          <p className="text-[11px] text-green-700 mt-1">{t("seller_shipments.credit_booked")}</p>
        </div>
      </div>
    </InfoCard>
  );
}
